import { AdvancedSelect } from '../../../../../widgets/select';
import { Checkbox } from '../../../../../widgets/checkbox';
import { Columns } from '../../../../../widgets/columns';
import { FormControl } from '../../../../../widgets/formControl';
import { Icon } from '../../../../../widgets/icon';
import { InputDialog, useDialogManager } from '../../../../../widgets/dialog';
import { Link, PrimaryButton } from '../../../../../widgets/buttons';
import { NotificationBanner } from '../../../../../widgets/notificationBanner';
import { PageFooterButtons } from '../../../../../widgets/headers';
import { Radio } from '../../../../../widgets/radio';
import { Section } from '../../../../../widgets/section';
import { SwitchGroup } from '../../../../../widgets/switchGroup';
import { ZTooltip } from '../../../../../widgets/ztooltip';
import { showErrorNotification } from '../../../../../widgets/notificationToaster';
import React, { FC, Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Unit, getStartingUnit, units } from 'src/components/widgets/areaDesigner/helpers';
import { EditableModelOptions, createDefaultEditableModelOptions } from 'src/components/widgets/areaDesigner/models';
import { editableModelOptionsToModelOptions, modelOptionsToEditableModelOptions } from 'src/components/widgets/areaDesigner/modelsAdapters';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { InputWithValidation, OnlyNewUI } from 'src/shared/globals';
import { T, stringFormat, validateInputSecurity } from 'src/shared/helpers';
import AddIcon from 'src/shared/icons/plus-circle-solid.svg';
import MdDelete from 'src/shared/icons/trash-alt.svg';
import { ValidationStateProvider, isStepAligned, validateRequired, validationContext } from 'src/shared/validation';
import styled from 'styled-components';
import {
    editProductNameAction,
    getResizableModelAttributes,
    mergeModelSideAction,
    setProductProviderCodeAction,
    toggleArForResizableAction,
    useProductConfigurationState
} from '../../../state';
import { createAreaFromSizePX, resizeArea } from '../../../state/model/modelArea';
import { ModelColor } from '../../../state/model/modelColor';
import { ModelOptions } from '../../../state/model/modelOptions';
import { ModelSide } from '../../../state/model/modelSide';
import { ProductMirrorTypes } from '../../../state/queries/__generated__/productFragment_model.graphql';
import ProductBackgroundSection from './productBackgroundSection';
import { ResizablePrintAreaContextProvider, ResizableSideType, useResizablePrintAreaContext } from './resizablePrintAreaContext';
import ShapeTypeSection from './resizableShapeTypeSection';
import ResizableSideSection from './resizableSideSection';

import borderTypeExampleImage from 'assets/images/others/border-color-senza-ombra.png';
import update from 'immutability-helper';
import _ from 'lodash';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { OptionProps } from 'react-select';
import ColorSelection from 'src/components/widgets/colorSelection';
import OverlayLoading from 'src/components/widgets/overlayLoading';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import JSONIcon from 'src/shared/icons/json-icon.svg';
import { resizablePrintAreaQuery } from './__generated__/resizablePrintAreaQuery.graphql';
import { PageMenu, PageMenuContentContainer, PageMenuLayoutContainer } from 'src/components/widgets/pageMenu';
import type { PageMenuSection, ValidationError } from 'src/components/widgets/pageMenu';

const edgeTypeOptions = [
    { value: 'COLORED', label: T._('Solid Color', 'Admin') },
    { value: 'MIRRORED', label: T._('Mirror Wrap', 'Admin') },
    { value: 'NONE', label: T._('Same as Background', 'Admin') }
];

const FormControlNameStyled = styled(FormControl)`
    margin-bottom: 0 !important;
`;

const JSONIconContainer = styled(Icon)`
    width: 40px;
    height: 40px;
`;

const NameWithIconContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 24px;
    align-items: flex-start;
`;

export const ParagraphTitle = styled.p`
    font-weight: bold;
    font-size: 16px;
    text-align: left;
    margin: 0 0 10px;
`;

export const ParagraphSubTitle = styled.span`
    line-height: 20px;
    color: #263238;
    display: block;
    margin-bottom: 15px;
`;

export const ResizableRadio = styled(Radio)`
    > label {
        > div {
            margin-left: 3px;
            padding-top: 0;
        }
    }
`;

const Column = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ColumnWithBorder = styled(Column)`
    border-radius: 8px;
    border: 1px solid rgba(219, 226, 230, 1);
    padding: 24px 24px 0px 24px;
`;

const FormControlWithMarginTop = styled(FormControl)`
    margin-top: 18px;
`;

const PresetColumn = styled.div`
    display: flex;
    flex-direction: column;
`;

const ResizableProductSection = styled(Section)`
    margin-bottom: 32px;
    border-radius: 16px;
    > div {
        padding: 40px;
    }
`;

const ResizableProductSectionName = styled(ResizableProductSection)`
    > div {
        padding: 40px 40px 25px 40px;
    }
`;

const BannerContainer = styled.div`
    margin-top: 40px;
`;

const IconContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1rem;
`;

const VerticalSeparator = styled.div`
    width: 0px;
    height: 24px;
    border: 1px solid rgba(0, 0, 0, 0.1);
`;

const PresetsRowContainer = styled.div`
    display: grid;
    grid-template-columns: 1.5fr 1fr 1fr 1fr auto;
    gap: 1rem;
`;

const PresetsRowHeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
`;

const LinkStyled = styled(Link)`
    cursor: pointer;
    text-decoration: unset;
    color: ${({ theme }) => theme.app.secondary};
    font-weight: bold;
`;

const StyledImg = styled.img`
    width: 60%;
    height: auto;
`;

const ConstraintTypeSection = styled.div`
    margin-bottom: 16px;
`;

const BorderOption: FC<OptionProps<{ value: string; label: string; tooltip?: string }, false>> = (props) => {
    return (
        <ZTooltip title={props.data.tooltip}>
            <div
                style={{
                    ...(props.getStyles('option', props) as Record<string, string>),
                    position: 'relative',
                    paddingRight: 30
                }}
                {...props.innerProps}
            >
                {props.data.label}
            </div>
        </ZTooltip>
    );
};

// Main Layout Container

// Section content visibility
const ResizableSectionContent = styled.div<{ $visible?: boolean }>`
    display: ${(props) => (props.$visible ? 'block' : 'none')};
`;

interface IResizablePrintAreaProps {
    onSave: (modelOptions: ModelOptions[]) => void;
}

const ResizablePrintAreaContent = ({ onSave }: IResizablePrintAreaProps) => {
    const query = useLazyLoadQuery<resizablePrintAreaQuery>(
        graphql`
            query resizablePrintAreaQuery {
                resizablePresets {
                    nodes {
                        id
                        resizablePresetID
                        name
                        width
                        height
                    }
                }
            }
        `,
        {},
        { fetchPolicy: 'store-and-network' }
    );

    const presets = useMemo(() => {
        return (
            query.resizablePresets?.nodes?.map((preset) => ({
                label: preset.name || '',
                value: preset.name || '',
                width: preset.width || 0,
                height: preset.height || 0
            })) || []
        );
    }, [query.resizablePresets]);

    const validation = useContext(validationContext);
    const { state, dispatch } = useProductConfigurationState();
    const [modelColor, modelSide, modelOptions] = getResizableModelAttributes(state.product);
    const [unit, setUnit] = useState<Unit>(getStartingUnit());

    const [providerCode, setProviderCode] = useState(state.product.providerCode);
    const { showDialog, closeDialog } = useDialogManager();

    const { userInfo } = useAccountData();
    const isJsonProductsImportFeatureEnabled = useFeatureFlagWithConditions('jsonProductsImport', {
        userId: userInfo?.userID?.toString()
    });

    // Side menu contents with dynamic error tracking
    const [menuSections, setMenuSections] = useState<PageMenuSection[]>([
        {
            key: 'product',
            label: T._('Product', 'Admin'),
            errors: [],
            subSections: [
                {
                    key: 'sides',
                    label: T._('Sides', 'Admin'),
                    errors: []
                },
                {
                    key: 'shape',
                    label: T._('Shape', 'Admin'),
                    errors: []
                },
                {
                    key: 'measurements',
                    label: T._('Measurements', 'Admin'),
                    errors: []
                }
            ]
        },
        {
            key: 'background-border',
            label: T._('Background and Border', 'Admin'),
            errors: []
        },
        {
            key: '3d-preview',
            label: T._('3D Preview', 'Admin'),
            errors: []
        },
        {
            key: 'multi-canvas',
            label: T._('Multi Canvas', 'Admin'),
            errors: []
        },
        {
            key: 'preset-options',
            label: T._('Preset Options', 'Admin'),
            errors: []
        }
    ]);

    const buildInitialEditableModelOptions = (
        modelColor: ModelColor,
        modelSide: ModelSide,
        modelOptions: ModelOptions | null,
        unit: Unit
    ): EditableModelOptions => {
        let editableModelOptions: EditableModelOptions;

        if (modelOptions) {
            editableModelOptions = modelOptionsToEditableModelOptions(modelOptions, modelSide.ppcm, unit);
            editableModelOptions.isResizable = true;
        } else {
            editableModelOptions = createDefaultEditableModelOptions(modelColor, null);
            editableModelOptions.isResizable = true;
        }

        return editableModelOptions;
    };

    const [editableModelOptions, setEditableModelOptions] = useState<EditableModelOptions>(
        buildInitialEditableModelOptions(modelColor, modelSide, modelOptions, unit)
    );

    const { sideType } = useResizablePrintAreaContext();

    // Function to get validation errors for a specific section
    function getValidationErrorsForSection(sectionKey: string, subSectionKey?: string): ValidationError[] {
        const errors: ValidationError[] = [];

        // Define validation key mappings for each section/subsection
        const validationKeyMappings: Record<string, string[]> = {
            product: ['validationProductName', 'side'],
            sides: [],
            shape: [], // Shape section typically doesn't have validation errors
            measurements: [
                'validationResizableMinWidth',
                'validationResizableMaxWidth',
                'validationResizableStartWidth',
                'validationResizableMinHeight',
                'validationResizableMaxHeight',
                'validationResizableStartHeight',
                'validationResizableMinArea',
                'validationResizableMaxArea',
                'validationResizableStartWidthArea',
                'validationResizableStartHeightArea',
                'validationResizableMinSide',
                'validationResizableStartSide',
                'validationResizableMaxSizeForSide',
                'validationResizableMaxSide',
                'validationResizableStep'
            ],
            'background-border': ['validationResizableDepth'],
            'multi-canvas': ['validationMinColumnWidth', 'validationStartColumns', 'validationMinRowHeight', 'validationStartRows'],
            '3d-preview': [],
            'preset-options': []
        };

        // Define human-readable field names for validation keys
        const getFieldName = (validationKey: string): string => {
            const fieldNameMappings: Record<string, string> = {
                validationProductName: T._('Product Name', 'Admin'),
                validationResizableMinWidth: T._('Min Width', 'Admin'),
                validationResizableMaxWidth: T._('Max Width', 'Admin'),
                validationResizableStartWidth: T._('Start Width', 'Admin'),
                validationResizableMinHeight: T._('Min Height', 'Admin'),
                validationResizableMaxHeight: T._('Max Height', 'Admin'),
                validationResizableStartHeight: T._('Start Height', 'Admin'),
                validationResizableMinArea: T._('Min Area', 'Admin'),
                validationResizableMaxArea: T._('Max Area', 'Admin'),
                validationResizableStartWidthArea: T._('Start Width (Area)', 'Admin'),
                validationResizableStartHeightArea: T._('Start Height (Area)', 'Admin'),
                validationResizableMinSide: T._('Min Side', 'Admin'),
                validationResizableStartSide: T._('Start Side', 'Admin'),
                validationResizableMaxSizeForSide: T._('Max Size for Side', 'Admin'),
                validationResizableMaxSide: T._('Max Side', 'Admin'),
                validationResizableStep: T._('Step Size', 'Admin'),
                validationResizableDepth: T._('3D Depth', 'Admin'),
                validationMinColumnWidth: T._('Min Column Width', 'Admin'),
                validationStartColumns: T._('Start Columns', 'Admin'),
                validationMinRowHeight: T._('Min Row Height', 'Admin'),
                validationStartRows: T._('Start Rows', 'Admin')
            };

            // Handle dynamic preset field names
            if (validationKey.startsWith('validationPresetName')) {
                const index = validationKey.replace('validationPresetName', '');
                return `${T._('Preset Name', 'Admin')} ${parseInt(index) + 1}`;
            }
            if (validationKey.startsWith('validationPresetWidth')) {
                const index = validationKey.replace('validationPresetWidth', '');
                return `${T._('Preset Width', 'Admin')} ${parseInt(index) + 1}`;
            }
            if (validationKey.startsWith('validationPresetHeight')) {
                const index = validationKey.replace('validationPresetHeight', '');
                return `${T._('Preset Height', 'Admin')} ${parseInt(index) + 1}`;
            }
            if (validationKey.startsWith('side')) {
                const index = validationKey.replace('side', '');
                return `${T._('Side Name', 'Admin')} ${parseInt(index) + 1}`;
            }

            return fieldNameMappings[validationKey] || validationKey;
        };

        // Add dynamic preset validation keys
        if (editableModelOptions.presets && (sectionKey === 'preset-options' || sectionKey === 'product')) {
            editableModelOptions.presets.forEach((_, idx) => {
                validationKeyMappings['preset-options'].push(`validationPresetName${idx}`, `validationPresetWidth${idx}`, `validationPresetHeight${idx}`);
            });
        }

        if (state.sides && (sectionKey === 'sides' || sectionKey === 'product')) {
            state.sides.forEach((_, idx) => {
                validationKeyMappings['sides'].push(`side${idx}`);
            });
        }

        // Get validation keys for the specific section or subsection
        const relevantKeys = subSectionKey ? validationKeyMappings[subSectionKey] || [] : validationKeyMappings[sectionKey] || [];

        // Check for errors in validation context
        relevantKeys.forEach((key) => {
            const error = validation.validations.find((v) => v.validationKey === key);
            if (error && error.message) {
                errors.push({
                    fieldName: getFieldName(key),
                    message: error.message
                });
            }
        });

        return errors;
    }

    // Menu navigation state
    const [activeMainSection, setActiveMainSection] = useState<string>('product');
    const [activeSubSection, setActiveSubSection] = useState<string | null>(null); // null means show all subsections
    const [isScrolling, setIsScrolling] = useState(false); // Flag to prevent conflicts between manual clicks and scroll detection

    // Handle main section change
    const handleMainSectionChange = (section: string) => {
        setActiveMainSection(section);

        // When clicking main section, show all subsections (set activeSubSection to null only if not in product section)
        // For product section, keep the current activeSubSection to maintain scroll-based selection
        if (section !== 'product') {
            setActiveSubSection(null);
        }

        window.scrollTo({ top: 0 });
    };

    // Handle sub-section change
    const handleSubSectionChange = (subSection: string) => {
        let relatedMainSection = menuSections.find((item) => item.subSections?.map((sub) => sub.key).includes(subSection));
        if (relatedMainSection) {
            setActiveMainSection(relatedMainSection.key);
        }

        setActiveSubSection(subSection);

        // Set scrolling flag to prevent scroll listener from interfering
        setIsScrolling(true);

        // Scroll to the anchor
        setTimeout(() => {
            const element = document.getElementById(subSection);
            if (element) {
                const elementPosition = element.offsetTop;
                const offsetPosition = elementPosition - 190;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }

            // Reset scrolling flag after scroll animation
            setTimeout(() => setIsScrolling(false), 1000);
        }, 100);
    };

    // Scroll detection for automatic sub-section highlighting
    const edgeTypeOptionsToDisplay = useMemo(() => {
        return !(sideType === 'double-syn')
            ? edgeTypeOptions
            : edgeTypeOptions.map((option) => ({
                  ...option,
                  ...(option.value === 'MIRRORED' && {
                      tooltip: T._('Mirror wrap option is not available for front and back products.', 'Admin'),
                      isDisabled: true
                  })
              }));
    }, [sideType]);

    const setEditableModelOptionsDebounced = useCallback(
        _.debounce((editableModelOptions: EditableModelOptions) => {
            setEditableModelOptions({
                ...editableModelOptions
            });
        }, 300),
        []
    );

    const handleClick3DPreview = (checked: boolean) => {
        if (!modelOptions) return;

        setEditableModelOptions({ ...editableModelOptions, is3DAutoGenEnabled: checked });

        dispatch(toggleArForResizableAction(checked));
    };

    const handleClickIsArEnabled = (checked: boolean) => {
        if (!modelOptions) return;

        dispatch(toggleArForResizableAction(checked));
    };

    const handleChangeLockWidth = (checked: boolean) => {
        if (!editableModelOptions) return;

        setEditableModelOptions({ ...editableModelOptions, lockWidth: checked, minWidth: '', maxWidth: '' });
    };

    const handleChangeLockHeight = (checked: boolean) => {
        if (!editableModelOptions) return;

        setEditableModelOptions({ ...editableModelOptions, lockHeight: checked, minHeight: '', maxHeight: '' });
    };

    const handleClickKeepAspectRatio = (checked: boolean) => {
        if (!modelOptions) return;

        setEditableModelOptions({ ...editableModelOptions, keepAspectRatio: checked });
    };

    const handleIncludeBorderInPrintFiles = (checked: boolean) => {
        if (!modelOptions) return;

        setEditableModelOptions({ ...editableModelOptions, includeBorderInPrintFiles: checked });
    };

    const handleResizableAreaChanged = (widthPX: number, heightPX: number) => {
        if (isNaN(widthPX) || isNaN(heightPX) || widthPX <= 0 || heightPX <= 0) return;

        // Prevent the resizable area from being too big
        //const ppcm = MAX_RESIZABLE_AREA_IN_PX / Math.max(width, height);

        let newModelSide: ModelSide;

        if (modelSide.areas.length > 0 && modelSide.areas[0].areaID != null) {
            // update existing area
            const areaToUpdate = modelSide.areas[0];

            newModelSide = update(modelSide, {
                areas: {
                    [0]: {
                        $set: resizeArea(areaToUpdate, widthPX, heightPX)
                    }
                }
            });
        } else {
            // create new area
            newModelSide = update(modelSide, {
                /*'ppcm': { $set: ppcm },*/
                ['areas']: {
                    $set: [
                        {
                            ...createAreaFromSizePX(widthPX, heightPX)
                        }
                    ]
                }
            });
        }

        dispatch(mergeModelSideAction(modelSide, newModelSide));
    };

    const handleChangePresetType = (e: (typeof presets)[0], idx: number) => {
        const preset = editableModelOptions.presets?.[idx];
        if (!preset) return;

        const cmUnit = units.find((u) => u.value === 'cm')!;

        preset.name = e.value;
        preset.width = e.width ? (Math.round(((e.width * cmUnit.ratio) / unit.ratio) * 100) / 100).toString() : '0';
        preset.height = e.height ? (Math.round(((e.height * cmUnit.ratio) / unit.ratio) * 100) / 100).toString() : '0';

        setEditableModelOptions({ ...editableModelOptions });
    };

    const handleChangePresetName = (name: string, idx: number) => {
        const preset = editableModelOptions.presets?.[idx];
        if (!preset) return;
        preset.name = name;

        setEditableModelOptions({ ...editableModelOptions });
    };

    const handleChangePresetWidth = (width: string, idx: number) => {
        const preset = editableModelOptions.presets?.[idx];
        if (!preset) return;
        preset.width = width;

        setEditableModelOptions({ ...editableModelOptions });
    };

    const handleChangePresetHeight = (height: string, idx: number) => {
        const preset = editableModelOptions.presets?.[idx];
        if (!preset) return;
        preset.height = height;

        setEditableModelOptions({ ...editableModelOptions });
    };

    const handleAddPreset = (idx: number) => {
        if (editableModelOptions.presets != null && editableModelOptions.presets?.length > 0 && idx !== editableModelOptions.presets?.length - 1) {
            return;
        }

        const newEditableModelOptions = { ...editableModelOptions };
        const newPresets = newEditableModelOptions.presets || [];
        newPresets!.splice(idx + 1, 0, { name: '', width: '', height: '' });
        newEditableModelOptions.presets = newPresets;

        setEditableModelOptions(newEditableModelOptions);
    };

    const handleDeletePreset = (idx: number) => {
        const newEditableModelOptions = { ...editableModelOptions };
        newEditableModelOptions.presets!.splice(idx, 1);

        setEditableModelOptions(newEditableModelOptions);

        validation.remove('validationPresetName' + idx);
        validation.remove('validationPresetWidth' + idx);
        validation.remove('validationPresetHeight' + idx);
    };

    const handleChangeProductName = (value: string) => {
        dispatch(editProductNameAction(value));
    };

    const [constraintType, setConstraintType] = useState<'different' | 'flexibile' | 'area'>(() => {
        if (!state.product.id) return 'different';

        if (modelOptions?.minArea || modelOptions?.maxArea) {
            return 'area';
        }
        return modelOptions?.maxSizeForSide ? 'flexibile' : 'different';
    });

    const handleClickJsonSetting = () => {
        showDialog(
            'JSON-settings-dialog',
            <InputDialog
                label={T._('Entity Reference Key', 'Admin')}
                defaultValue={providerCode ?? ''}
                onConfirm={(providerCode) => {
                    if (providerCode === '') {
                        showErrorNotification({
                            message: T._('Type a valid provider code', 'Admin')
                        });

                        return;
                    }

                    closeDialog('JSON-settings-dialog');

                    setProviderCode(providerCode);
                    dispatch(setProductProviderCodeAction(providerCode));
                }}
            />
        );
    };

    // Use Effects

    // Update menu sections with validation errors whenever validation context changes
    useEffect(() => {
        setMenuSections((prevSections) =>
            prevSections.map((section) => ({
                ...section,
                errors: getValidationErrorsForSection(section.key),
                subSections: section.subSections?.map((subSection) => ({
                    ...subSection,
                    errors: getValidationErrorsForSection(section.key, subSection.key)
                }))
            }))
        );
    }, [editableModelOptions, state.product.name, ...state.sides.map((s) => s.name)]);

    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    // Only detect scroll when in product section and not manually scrolling
                    if (activeMainSection !== 'product' || isScrolling) {
                        ticking = false;
                        return;
                    }

                    const productSubSections = menuSections.find((section) => section.key === 'product')?.subSections?.map((sub) => sub.key) || [];

                    // Get all section elements and their positions
                    const sectionElements = productSubSections
                        .map((subKey) => ({
                            key: subKey,
                            element: document.getElementById(subKey)
                        }))
                        .filter((item) => item.element !== null);

                    if (sectionElements.length === 0) {
                        ticking = false;
                        return;
                    }

                    // Find which section is currently in view
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    const scrollOffset = 200; // Offset for better detection

                    // If scrolled to very top (within first 100px), reset to no specific subsection
                    if (scrollTop <= 100) {
                        if (activeSubSection !== null) {
                            setActiveSubSection(null);
                        }
                        ticking = false;
                        return;
                    }

                    let activeSection: string | null = null;

                    for (const { key, element } of sectionElements) {
                        if (!element) continue;

                        const elementTop = element.offsetTop;
                        const elementBottom = elementTop + element.offsetHeight;

                        // Check if section is in viewport
                        if (elementTop <= scrollTop + scrollOffset && elementBottom > scrollTop + scrollOffset) {
                            activeSection = key;
                            break;
                        }
                    }

                    // If no section is specifically in view, check which one is closest to the top
                    if (!activeSection) {
                        let closestDistance = Infinity;
                        for (const { key, element } of sectionElements) {
                            if (!element) continue;

                            const elementTop = element.offsetTop;
                            const distance = Math.abs(elementTop - (scrollTop + scrollOffset));

                            if (distance < closestDistance) {
                                closestDistance = distance;
                                activeSection = key;
                            }
                        }
                    }

                    // Update active sub-section if it changed
                    if (activeSection && activeSection !== activeSubSection) {
                        setActiveSubSection(activeSection);
                    }

                    ticking = false;
                });
                ticking = true;
            }
        };

        // Add scroll listener
        window.addEventListener('scroll', handleScroll, { passive: true });

        // Call once on mount to set initial state, with a small delay to ensure DOM is ready
        setTimeout(() => {
            if (activeMainSection === 'product') {
                handleScroll();
            }
        }, 100);

        // Cleanup
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [activeMainSection, activeSubSection, isScrolling]);

    // disable the mirrored edge type if the side type is double-syn
    useEffect(() => {
        if (sideType === 'double-syn') {
            setEditableModelOptions((prev) => ({ ...prev, mirrorType: 'COLORED', edgesColor: '#FFFFFF' }));
        }
    }, [sideType]);

    // set the default values for startWidth and startHeight if they are not set with an average value between min and max
    useEffect(() => {
        if (
            parseFloat(editableModelOptions.minWidth) > 0 &&
            parseFloat(editableModelOptions.maxWidth) > 0 &&
            parseFloat(editableModelOptions.minWidth) < parseFloat(editableModelOptions.maxWidth) &&
            !(parseFloat(editableModelOptions.startWidth) > 0)
        ) {
            setEditableModelOptionsDebounced({
                ...editableModelOptions,
                startWidth: Math.round(((parseFloat(editableModelOptions.minWidth) + parseFloat(editableModelOptions.maxWidth)) / 2) * 100) / 100 + ''
            });
        }
    }, [editableModelOptions.minWidth, editableModelOptions.maxWidth]);

    useEffect(() => {
        if (
            parseFloat(editableModelOptions.minHeight) > 0 &&
            parseFloat(editableModelOptions.maxHeight) > 0 &&
            parseFloat(editableModelOptions.minHeight) < parseFloat(editableModelOptions.maxHeight) &&
            !(parseFloat(editableModelOptions.startHeight) > 0)
        ) {
            setEditableModelOptionsDebounced({
                ...editableModelOptions,
                startHeight: Math.round(((parseFloat(editableModelOptions.minHeight) + parseFloat(editableModelOptions.maxHeight)) / 2) * 100) / 100 + ''
            });
        }
    }, [editableModelOptions.minHeight, editableModelOptions.maxHeight]);

    // Automatically disable Multi-canvas when shape is set to 1 (rounded)
    useEffect(() => {
        if (editableModelOptions.shape === 1) {
            setEditableModelOptionsDebounced({
                ...editableModelOptions,
                isSplitOutput: false,
                startSplitColumns: '1',
                startSplitRows: '1',
                minSplitHeight: '1',
                minSplitWidth: '1'
            });
        }
    }, [editableModelOptions.shape]);

    // Automatically disable Multi-canvas and 3D preview when background is enabled
    // useEffect(() => {
    // 	const isBackgroundEnabled = (editableModelOptions.backgroundType || 0) > 0;

    // 	if (isBackgroundEnabled) {
    // 		// If background is enabled, disable both Multi-canvas and 3D preview
    // 		if (editableModelOptions.isSplitOutput || editableModelOptions.is3DAutoGenEnabled) {
    // 			setEditableModelOptions((prev) => ({
    // 				...prev,
    // 				isSplitOutput: false,
    // 				is3DAutoGenEnabled: false
    // 			}));

    // 			// Also update AR state if 3D was enabled
    // 			if (editableModelOptions.is3DAutoGenEnabled) {
    // 				dispatch(toggleArForResizableAction(false));
    // 			}
    // 		}
    // 	}
    // }, [editableModelOptions.backgroundType]);

    return (
        <PageMenuLayoutContainer>
            {/* Vertical Navigation Menu */}
            <PageMenu
                activeSection={activeMainSection}
                activeSubSection={activeSubSection}
                sections={menuSections}
                onSectionClick={handleMainSectionChange}
                onSubSectionClick={handleSubSectionChange}
            />

            {/* Main Content */}
            <PageMenuContentContainer>
                {/* Products Section */}
                <ResizableSectionContent id='product' $visible={activeMainSection === 'product'}>
                    {!state.product.imageUrl && state.ecommerceSalesChannel.some((e) => e === 'NONE') && (
                        <ResizableProductSectionName id='name'>
                            {/* show only if from scratch */}
                            <ParagraphTitle>{T._('Name', 'Admin')}</ParagraphTitle>
                            <NameWithIconContainer>
                                <FormControlNameStyled>
                                    <InputWithValidation
                                        validationKey={'validationProductName'}
                                        validate={(value) => validateRequired(value, false) || !validateInputSecurity(value).isValid}
                                        message={`${validateRequired(state.product.name, false) ? T._('Required', 'Admin') : !validateInputSecurity(state.product.name).isValid ? T._(`Invalid characters detected: ${validateInputSecurity(state.product.name).invalidCharacters.join(', ')}`, 'Admin') : ''}`}
                                        value={state.product.name}
                                        fullWidth
                                        onChange={(e) => handleChangeProductName(e.target.value)}
                                    />
                                </FormControlNameStyled>
                                {isJsonProductsImportFeatureEnabled && (
                                    <ZTooltip
                                        position='left'
                                        title={T._(
                                            "Edit JSON setting - This setting is only needed for bulk import functionality to reference the entity in the import JSON. If you don't need the JSON bulk import functionality, you can ignore this parameter.",
                                            'Admin'
                                        )}
                                    >
                                        <JSONIconContainer $hoverable onClick={handleClickJsonSetting}>
                                            <JSONIcon />
                                        </JSONIconContainer>
                                    </ZTooltip>
                                )}
                            </NameWithIconContainer>
                        </ResizableProductSectionName>
                    )}

                    {/* Sides Subsection - Visible when Products section is active and either no specific subsection is selected or 'sides' is selected */}
                    <ResizableProductSection id='sides'>
                        <ParagraphTitle>{T._('Sides', 'Admin')}</ParagraphTitle>
                        <ParagraphSubTitle>
                            {T._('Start by selecting how many ', 'Admin')}
                            <Link
                                href='https://zakeke.zendesk.com/hc/en-us/articles/14234024887964-Resizable-products#h_01K33E4FTV4XQS8JT9CHNZNP31'
                                target='_blank'
                            >
                                {T._('sides', 'Admin')}
                            </Link>
                            {T._(' the product will have.', 'Admin')}
                        </ParagraphSubTitle>
                        <ResizableSideSection />
                    </ResizableProductSection>

                    {/* Shape Subsection - Visible when Products section is active */}
                    <ResizableProductSection id='shape'>
                        <ParagraphTitle>{T._('Shape', 'Admin')}</ParagraphTitle>
                        <ParagraphSubTitle>
                            {T._('Choose the ', 'Admin')}
                            <Link
                                href='https://zakeke.zendesk.com/hc/en-us/articles/14234024887964-Resizable-products#h_01K33CMT3RFXHHRSWY56RD609P'
                                target='_blank'
                            >
                                {T._('product shape', 'Admin')}
                            </Link>
                        </ParagraphSubTitle>
                        <Column>
                            <ShapeTypeSection
                                selectedShape={editableModelOptions.shape || 0}
                                onShapeChange={(shape) => {
                                    setEditableModelOptions((prev) => ({
                                        ...prev,
                                        shape
                                    }));
                                }}
                            />
                        </Column>
                    </ResizableProductSection>

                    {/* Measurements Subsection - Visible when Products section is active */}
                    <ResizableProductSection id='measurements'>
                        <ParagraphTitle>{T._('Measurements', 'Admin')}</ParagraphTitle>
                        <ParagraphSubTitle>
                            {T._(
                                'Define how customers can resize your product. You can control whether each side is fixed or resizable, set minimum and maximum size limits, and decide how the sides interact with each other.',
                                'Admin'
                            )}
                            &nbsp;
                            <Link
                                href='https://zakeke.zendesk.com/hc/en-us/articles/14234024887964-Resizable-products#h_01K33ER46QWSV99K2SSY5PAWNP'
                                target='_blank'
                            >
                                {T._('Detailed guide.', 'Admin')}
                            </Link>
                        </ParagraphSubTitle>

                        <Columns $xs={6}>
                            <FormControl
                                label={T._('Measure unit', 'Admin')}
                                tooltipLabelHtml={
                                    <span>
                                        {T._(
                                            'The unit of measure selected here only affects the configuration of this tool. It does not determine what the end user will see in the interface. The unit of measure displayed to users is defined as a global setting in Settings > User Interface Settings > Unit of Measure.',
                                            'Admin'
                                        )}
                                    </span>
                                }
                            >
                                <AdvancedSelect
                                    isSearchable={false}
                                    options={units}
                                    placeholder={T._('Select unit...', 'Admin')}
                                    onChange={(e) => {
                                        const currentUnit = unit;
                                        const newUnit = e as (typeof units)[number];
                                        setUnit(newUnit);

                                        // Convert all current resizable options to the new unit
                                        if (editableModelOptions) {
                                            let newEditableModelOptions: EditableModelOptions = {
                                                ...editableModelOptions,
                                                startWidth: editableModelOptions.startWidth
                                                    ? (
                                                          Math.round(
                                                              ((parseFloat(editableModelOptions.startWidth) * currentUnit.ratio) / newUnit.ratio) * 100
                                                          ) / 100
                                                      ).toString()
                                                    : '',
                                                startHeight: editableModelOptions.startHeight
                                                    ? (
                                                          Math.round(
                                                              ((parseFloat(editableModelOptions.startHeight) * currentUnit.ratio) / newUnit.ratio) * 100
                                                          ) / 100
                                                      ).toString()
                                                    : '',
                                                minWidth: editableModelOptions.minWidth
                                                    ? (
                                                          Math.round(((parseFloat(editableModelOptions.minWidth) * currentUnit.ratio) / newUnit.ratio) * 100) /
                                                          100
                                                      ).toString()
                                                    : '',
                                                minHeight: editableModelOptions.minHeight
                                                    ? (
                                                          Math.round(((parseFloat(editableModelOptions.minHeight) * currentUnit.ratio) / newUnit.ratio) * 100) /
                                                          100
                                                      ).toString()
                                                    : '',
                                                maxWidth: editableModelOptions.maxWidth
                                                    ? (
                                                          Math.round(((parseFloat(editableModelOptions.maxWidth) * currentUnit.ratio) / newUnit.ratio) * 100) /
                                                          100
                                                      ).toString()
                                                    : '',
                                                maxHeight: editableModelOptions.maxHeight
                                                    ? (
                                                          Math.round(((parseFloat(editableModelOptions.maxHeight) * currentUnit.ratio) / newUnit.ratio) * 100) /
                                                          100
                                                      ).toString()
                                                    : '',
                                                depth: editableModelOptions.depth
                                                    ? (
                                                          Math.round(((parseFloat(editableModelOptions.depth) * currentUnit.ratio) / newUnit.ratio) * 100) / 100
                                                      ).toString()
                                                    : '',
                                                stepHeight: editableModelOptions.stepHeight
                                                    ? (
                                                          Math.round(
                                                              ((parseFloat(editableModelOptions.stepHeight) * currentUnit.ratio) / newUnit.ratio) * 100
                                                          ) / 100
                                                      ).toString()
                                                    : '',
                                                stepWidth: editableModelOptions.stepWidth
                                                    ? (
                                                          Math.round(((parseFloat(editableModelOptions.stepWidth) * currentUnit.ratio) / newUnit.ratio) * 100) /
                                                          100
                                                      ).toString()
                                                    : '',
                                                minSplitWidth: editableModelOptions.minSplitWidth
                                                    ? (
                                                          Math.round(
                                                              ((parseFloat(editableModelOptions.minSplitWidth) * currentUnit.ratio) / newUnit.ratio) * 100
                                                          ) / 100
                                                      ).toString()
                                                    : '',
                                                minSplitHeight: editableModelOptions.minSplitHeight
                                                    ? (
                                                          Math.round(
                                                              ((parseFloat(editableModelOptions.minSplitHeight) * currentUnit.ratio) / newUnit.ratio) * 100
                                                          ) / 100
                                                      ).toString()
                                                    : '',
                                                minArea: editableModelOptions.minArea
                                                    ? (
                                                          Math.round(
                                                              ((parseFloat(editableModelOptions.minArea) * currentUnit.ratio * currentUnit.ratio) /
                                                                  newUnit.ratio /
                                                                  newUnit.ratio) *
                                                                  100
                                                          ) / 100
                                                      ).toString()
                                                    : '',
                                                maxArea: editableModelOptions.maxArea
                                                    ? (
                                                          Math.round(
                                                              ((parseFloat(editableModelOptions.maxArea) * currentUnit.ratio * currentUnit.ratio) /
                                                                  newUnit.ratio /
                                                                  newUnit.ratio) *
                                                                  100
                                                          ) / 100
                                                      ).toString()
                                                    : ''
                                            };

                                            //change also the presets sizes
                                            if (editableModelOptions.presets) {
                                                newEditableModelOptions.presets = editableModelOptions.presets.map((preset) => {
                                                    return {
                                                        ...preset,
                                                        width: (
                                                            Math.round(((parseFloat(preset.width) * currentUnit.ratio) / newUnit.ratio) * 100) / 100
                                                        ).toString(),
                                                        height: (
                                                            Math.round(((parseFloat(preset.height) * currentUnit.ratio) / newUnit.ratio) * 100) / 100
                                                        ).toString()
                                                    };
                                                });
                                            }

                                            setEditableModelOptions(newEditableModelOptions);
                                        }

                                        try {
                                            localStorage.setItem('area_designer_settings', JSON.stringify({ unit: newUnit.value }));
                                        } catch {}
                                    }}
                                    isMulti={false}
                                    value={unit}
                                />
                            </FormControl>
                        </Columns>

                        <ConstraintTypeSection>
                            <ParagraphTitle style={{ marginTop: 40 }}>{T._('Size limitation mode', 'Admin')}</ParagraphTitle>
                            <ResizableRadio
                                name='constraintType'
                                value='different'
                                label={T._('Independent Width & Height Limits', 'Admin')}
                                caption={T._(
                                    'Set separate minimum and maximum measure values for width and height. Each dimension follows its own independent restrictions.',
                                    'Admin'
                                )}
                                checked={constraintType === 'different'}
                                onSelect={() => {
                                    setConstraintType('different');
                                    // Remove validations from flexible constraint type
                                    validation.remove('validationResizableMinSide');
                                    validation.remove('validationResizableStartSide');
                                    validation.remove('validationResizableMaxSide');
                                    validation.remove('validationResizableMaxSizeForSide');
                                    // Remove validations from area constraint type
                                    validation.remove('validationResizableMinArea');
                                    validation.remove('validationResizableMaxArea');
                                    validation.remove('validationResizableStartWidthArea');
                                    validation.remove('validationResizableStartHeightArea');
                                }}
                            />
                            <ResizableRadio
                                name='constraintType'
                                value='single'
                                label={
                                    <span>
                                        {T._('Flexible Limits', 'Admin')}&nbsp;&nbsp;
                                        <OnlyNewUI />
                                    </span>
                                }
                                checked={constraintType === 'flexibile'}
                                caption={T._(
                                    'Define general limits that apply to either width or height. When one side reaches the limit, the other side is adjusted within the remaining allowed range.',
                                    'Admin'
                                )}
                                onSelect={() => {
                                    setEditableModelOptions({
                                        ...editableModelOptions,
                                        lockWidth: false,
                                        lockHeight: false,
                                        minHeight: editableModelOptions.minWidth,
                                        maxHeight: editableModelOptions.maxWidth,
                                        startHeight: editableModelOptions.startWidth,
                                        keepAspectRatio: false
                                    });

                                    setConstraintType('flexibile');
                                    // Remove validations from different constraint type
                                    validation.remove('validationResizableMinWidth');
                                    validation.remove('validationResizableMaxWidth');
                                    validation.remove('validationResizableStartWidth');
                                    validation.remove('validationResizableMinHeight');
                                    validation.remove('validationResizableMaxHeight');
                                    validation.remove('validationResizableStartHeight');
                                    // Remove validations from area constraint type
                                    validation.remove('validationResizableMinArea');
                                    validation.remove('validationResizableMaxArea');
                                    validation.remove('validationResizableStartWidthArea');
                                    validation.remove('validationResizableStartHeightArea');
                                }}
                            />

                            <ResizableRadio
                                name='constraintType'
                                value='area'
                                label={
                                    <span>
                                        {T._('Total Area Limit', 'Admin')}&nbsp;&nbsp;
                                        <OnlyNewUI />
                                    </span>
                                }
                                checked={constraintType === 'area'}
                                caption={stringFormat(
                                    T._(
                                        `Define size restrictions by setting minimum and maximum total area in {0}². Width and height can scale freely as long as their combined area stays within these limits.`,
                                        'Admin'
                                    ),
                                    unit.label
                                )}
                                onSelect={() => {
                                    setConstraintType('area');
                                    // Remove validations from different constraint type
                                    validation.remove('validationResizableMinWidth');
                                    validation.remove('validationResizableMaxWidth');
                                    validation.remove('validationResizableStartWidth');
                                    validation.remove('validationResizableMinHeight');
                                    validation.remove('validationResizableMaxHeight');
                                    validation.remove('validationResizableStartHeight');
                                    // Remove validations from flexible constraint type
                                    validation.remove('validationResizableMinSide');
                                    validation.remove('validationResizableStartSide');
                                    validation.remove('validationResizableMaxSide');
                                    validation.remove('validationResizableMaxSizeForSide');
                                }}
                            />
                        </ConstraintTypeSection>

                        {constraintType === 'different' ? (
                            <>
                                <Columns $xs={2}>
                                    <ColumnWithBorder>
                                        <ParagraphTitle> {T._('Width measure', 'Admin')}</ParagraphTitle>
                                        <div>
                                            <ResizableRadio
                                                name='productResizableWidth'
                                                value={'resizable'}
                                                label={T._('Resizable', 'Admin')}
                                                checked={!editableModelOptions.lockWidth}
                                                onSelect={() => handleChangeLockWidth(false)}
                                            />
                                            <ResizableRadio
                                                name='productResizableWidth'
                                                value={'fixed'}
                                                label={T._('Fixed', 'Admin')}
                                                checked={editableModelOptions.lockWidth}
                                                disabled={editableModelOptions.keepAspectRatio || editableModelOptions.lockHeight}
                                                onSelect={() => handleChangeLockWidth(true)}
                                            />
                                        </div>
                                        <Columns $xs={3}>
                                            <FormControl label={T._('Minimum measure', 'Admin')}>
                                                <InputWithValidation
                                                    validationKey={'validationResizableMinWidth'}
                                                    validate={(value) =>
                                                        !editableModelOptions.lockWidth &&
                                                        (validateRequired(value, false) ||
                                                            parseFloat(value) <= 0 ||
                                                            !isStepAligned(parseFloat(value), parseFloat(editableModelOptions.stepWidth)))
                                                    }
                                                    message={
                                                        !editableModelOptions.lockWidth &&
                                                        editableModelOptions.minWidth != '' &&
                                                        editableModelOptions.stepWidth != '' &&
                                                        !isStepAligned(parseFloat(editableModelOptions.minWidth), parseFloat(editableModelOptions.stepWidth))
                                                            ? T._('The value must match\nthe step increment', 'Admin')
                                                            : T._('Required', 'Admin')
                                                    }
                                                    type='number'
                                                    step='0.01'
                                                    min={0}
                                                    placeholder='1'
                                                    value={editableModelOptions.minWidth}
                                                    disabled={editableModelOptions.lockWidth}
                                                    fullWidth
                                                    onChange={(e) => setEditableModelOptions((prev) => ({ ...prev, minWidth: e.target.value }))}
                                                />
                                            </FormControl>
                                            <FormControl label={T._('Maximum measure', 'Admin')}>
                                                <InputWithValidation
                                                    validationKey={'validationResizableMaxWidth'}
                                                    validate={(value) =>
                                                        !editableModelOptions.lockWidth &&
                                                        (validateRequired(value, false) ||
                                                            parseFloat(value) <= 0 ||
                                                            !isStepAligned(parseFloat(value), parseFloat(editableModelOptions.stepWidth)))
                                                    }
                                                    message={
                                                        !editableModelOptions.lockWidth &&
                                                        editableModelOptions.maxWidth != '' &&
                                                        editableModelOptions.stepWidth != '' &&
                                                        !isStepAligned(parseFloat(editableModelOptions.maxWidth), parseFloat(editableModelOptions.stepWidth))
                                                            ? T._('The value must match\nthe step increment', 'Admin')
                                                            : T._('Required', 'Admin')
                                                    }
                                                    type='number'
                                                    step='0.01'
                                                    min={0}
                                                    placeholder='100'
                                                    value={editableModelOptions.maxWidth}
                                                    disabled={editableModelOptions.lockWidth}
                                                    fullWidth
                                                    onChange={(e) => setEditableModelOptions((prev) => ({ ...prev, maxWidth: e.target.value }))}
                                                />
                                            </FormControl>
                                            <FormControl label={editableModelOptions.lockWidth ? T._('Measure', 'Admin') : T._('Starting measure', 'Admin')}>
                                                <InputWithValidation
                                                    validationKey={'validationResizableStartWidth'}
                                                    validate={(value) =>
                                                        validateRequired(value, false) ||
                                                        parseFloat(value) <= 0 ||
                                                        !isStepAligned(parseFloat(value), parseFloat(editableModelOptions.stepWidth)) ||
                                                        (!editableModelOptions.lockWidth &&
                                                            ((parseFloat(editableModelOptions.maxWidth) > 0 &&
                                                                parseFloat(value) > parseFloat(editableModelOptions.maxWidth)) ||
                                                                (parseFloat(editableModelOptions.minWidth) > 0 &&
                                                                    parseFloat(value) < parseFloat(editableModelOptions.minWidth))))
                                                    }
                                                    message={
                                                        validateRequired(editableModelOptions.startWidth, false) ||
                                                        parseFloat(editableModelOptions.startWidth) <= 0
                                                            ? T._('Required', 'Admin')
                                                            : !isStepAligned(
                                                                    parseFloat(editableModelOptions.startWidth),
                                                                    parseFloat(editableModelOptions.stepWidth)
                                                                )
                                                              ? T._('The value must match\nthe step increment', 'Admin')
                                                              : T._(
                                                                    'The value should be higher\nthan the minimum width and\nlower than the maximum width',
                                                                    'Admin'
                                                                )
                                                    }
                                                    type='number'
                                                    step='0.01'
                                                    min={0}
                                                    placeholder='10'
                                                    value={editableModelOptions.startWidth}
                                                    fullWidth
                                                    onChange={(e) => {
                                                        setEditableModelOptions((prev) => ({ ...prev, startWidth: e.target.value }));
                                                    }}
                                                />
                                            </FormControl>
                                        </Columns>
                                    </ColumnWithBorder>
                                    <ColumnWithBorder>
                                        <ParagraphTitle>{T._('Height measure', 'Admin')}</ParagraphTitle>
                                        <div>
                                            <ResizableRadio
                                                name='productResizableHeight'
                                                value={'resizable'}
                                                label={T._('Resizable', 'Admin')}
                                                checked={!editableModelOptions.lockHeight}
                                                onSelect={() => handleChangeLockHeight(false)}
                                            />
                                            <ResizableRadio
                                                name='productResizableHeight'
                                                value={'fixed'}
                                                label={T._('Fixed', 'Admin')}
                                                checked={editableModelOptions.lockHeight}
                                                disabled={editableModelOptions.keepAspectRatio || editableModelOptions.lockWidth}
                                                onSelect={() => handleChangeLockHeight(true)}
                                            />
                                        </div>
                                        <Columns $xs={3}>
                                            <FormControl label={T._('Minimum measure', 'Admin')}>
                                                <InputWithValidation
                                                    validationKey={'validationResizableMinHeight'}
                                                    validate={(value) =>
                                                        !editableModelOptions.lockHeight &&
                                                        (validateRequired(value, false) ||
                                                            parseFloat(value) <= 0 ||
                                                            !isStepAligned(parseFloat(value), parseFloat(editableModelOptions.stepHeight)))
                                                    }
                                                    message={
                                                        !editableModelOptions.lockHeight &&
                                                        editableModelOptions.minHeight != '' &&
                                                        editableModelOptions.stepHeight != '' &&
                                                        !isStepAligned(parseFloat(editableModelOptions.minHeight), parseFloat(editableModelOptions.stepHeight))
                                                            ? T._('The value must match\nthe step increment', 'Admin')
                                                            : T._('Required', 'Admin')
                                                    }
                                                    type='number'
                                                    step='0.01'
                                                    min={0}
                                                    placeholder='1'
                                                    value={editableModelOptions.minHeight}
                                                    disabled={editableModelOptions.lockHeight}
                                                    fullWidth
                                                    onChange={(e) => setEditableModelOptions((prev) => ({ ...prev, minHeight: e.target.value }))}
                                                />
                                            </FormControl>
                                            <FormControl label={T._('Maximum measure', 'Admin')}>
                                                <InputWithValidation
                                                    validationKey={'validationResizableMaxHeight'}
                                                    validate={(value) =>
                                                        !editableModelOptions.lockHeight &&
                                                        (validateRequired(value, false) ||
                                                            parseFloat(value) <= 0 ||
                                                            !isStepAligned(parseFloat(value), parseFloat(editableModelOptions.stepHeight)))
                                                    }
                                                    message={
                                                        !editableModelOptions.lockHeight &&
                                                        editableModelOptions.maxHeight != '' &&
                                                        editableModelOptions.stepHeight != '' &&
                                                        !isStepAligned(parseFloat(editableModelOptions.maxHeight), parseFloat(editableModelOptions.stepHeight))
                                                            ? T._('The value must match\nthe step increment', 'Admin')
                                                            : T._('Required', 'Admin')
                                                    }
                                                    type='number'
                                                    step='0.01'
                                                    min={0}
                                                    placeholder='100'
                                                    value={editableModelOptions.maxHeight}
                                                    disabled={editableModelOptions.lockHeight}
                                                    fullWidth
                                                    onChange={(e) => setEditableModelOptions((prev) => ({ ...prev, maxHeight: e.target.value }))}
                                                />
                                            </FormControl>
                                            <FormControl label={editableModelOptions.lockHeight ? T._('Measure', 'Admin') : T._('Starting measure', 'Admin')}>
                                                <InputWithValidation
                                                    validationKey={'validationResizableStartHeight'}
                                                    validate={(value) =>
                                                        validateRequired(value, false) ||
                                                        parseFloat(value) <= 0 ||
                                                        !isStepAligned(parseFloat(value), parseFloat(editableModelOptions.stepHeight)) ||
                                                        (!editableModelOptions.lockHeight &&
                                                            ((parseFloat(editableModelOptions.maxHeight) > 0 &&
                                                                parseFloat(value) > parseFloat(editableModelOptions.maxHeight)) ||
                                                                (parseFloat(editableModelOptions.minHeight) > 0 &&
                                                                    parseFloat(value) < parseFloat(editableModelOptions.minHeight))))
                                                    }
                                                    message={
                                                        validateRequired(editableModelOptions.startHeight, false) ||
                                                        parseFloat(editableModelOptions.startHeight) <= 0
                                                            ? T._('Required', 'Admin')
                                                            : !isStepAligned(
                                                                    parseFloat(editableModelOptions.startHeight),
                                                                    parseFloat(editableModelOptions.stepHeight)
                                                                )
                                                              ? T._('The value must match\nthe step increment', 'Admin')
                                                              : T._(
                                                                    'The value should be higher\nthan the minimum height and\nlower than the maximum height',
                                                                    'Admin'
                                                                )
                                                    }
                                                    type='number'
                                                    step='0.01'
                                                    min={0}
                                                    placeholder='5'
                                                    value={editableModelOptions.startHeight}
                                                    fullWidth
                                                    onChange={(e) => {
                                                        setEditableModelOptions((prev) => ({ ...prev, startHeight: e.target.value }));
                                                    }}
                                                />
                                            </FormControl>
                                        </Columns>
                                    </ColumnWithBorder>
                                </Columns>
                            </>
                        ) : constraintType === 'area' ? (
                            <ColumnWithBorder>
                                <Columns $xs={2}>
                                    <FormControl
                                        label={stringFormat(T._('Minimum area', 'Admin'), unit.label)}
                                        caption={stringFormat(T._('Enter the smallest allowed total area in {0}².', 'Admin'), unit.label)}
                                    >
                                        <InputWithValidation
                                            validationKey={'validationResizableMinArea'}
                                            validate={(value) => validateRequired(value, false) || parseFloat(value) <= 0}
                                            message={
                                                validateRequired(editableModelOptions.minArea, false) || parseFloat(editableModelOptions.minArea) <= 0
                                                    ? T._('Required', 'Admin')
                                                    : ''
                                            }
                                            type='number'
                                            step='0.01'
                                            min={0}
                                            placeholder='1'
                                            value={editableModelOptions.minArea}
                                            fullWidth
                                            onChange={(e) => setEditableModelOptions((prev) => ({ ...prev, minArea: e.target.value }))}
                                        />
                                    </FormControl>
                                    <FormControl
                                        label={stringFormat(T._('Maximum area', 'Admin'), unit.label)}
                                        caption={stringFormat(T._('Enter the largest allowed total area in {0}².', 'Admin'), unit.label)}
                                    >
                                        <InputWithValidation
                                            validationKey={'validationResizableMaxArea'}
                                            validate={(value) => {
                                                const required = validateRequired(value, false);
                                                const minArea = parseFloat(editableModelOptions.minArea);
                                                const currentValue = parseFloat(value);

                                                if (required || currentValue <= 0) {
                                                    return true;
                                                }

                                                if (!isNaN(minArea) && !isNaN(currentValue) && minArea > currentValue) {
                                                    return true;
                                                }

                                                return false;
                                            }}
                                            message={
                                                validateRequired(editableModelOptions.maxArea, false) || parseFloat(editableModelOptions.maxArea) <= 0
                                                    ? T._('Required', 'Admin')
                                                    : T._('Maximum area must be greater than minimum area', 'Admin')
                                            }
                                            type='number'
                                            step='0.01'
                                            min={0}
                                            placeholder='100'
                                            value={editableModelOptions.maxArea}
                                            fullWidth
                                            onChange={(e) => setEditableModelOptions((prev) => ({ ...prev, maxArea: e.target.value }))}
                                        />
                                    </FormControl>
                                </Columns>
                                <Columns $xs={2}>
                                    <FormControl label={T._('Starting Width', 'Admin')} caption={T._('Set the default starting width dimension', 'Admin')}>
                                        <InputWithValidation
                                            validationKey={'validationResizableStartWidthArea'}
                                            validate={(value) => {
                                                if (validateRequired(value, false) || parseFloat(value) <= 0) {
                                                    return true;
                                                }

                                                const width = parseFloat(value);
                                                const height = parseFloat(editableModelOptions.startHeight);
                                                const minArea = parseFloat(editableModelOptions.minArea);
                                                const maxArea = parseFloat(editableModelOptions.maxArea);

                                                if (!isNaN(width) && !isNaN(height) && !isNaN(minArea) && !isNaN(maxArea)) {
                                                    const currentArea = width * height;
                                                    if (currentArea < minArea || currentArea > maxArea) {
                                                        return true;
                                                    }
                                                }

                                                if (!isStepAligned(width, parseFloat(editableModelOptions.stepWidth))) {
                                                    return true;
                                                }

                                                return false;
                                            }}
                                            message={
                                                validateRequired(editableModelOptions.startWidth, false) || parseFloat(editableModelOptions.startWidth) <= 0
                                                    ? T._('Required', 'Admin')
                                                    : !isStepAligned(parseFloat(editableModelOptions.startWidth), parseFloat(editableModelOptions.stepWidth))
                                                      ? T._('The value must match the step increment', 'Admin')
                                                      : stringFormat(
                                                            T._(
                                                                'The starting size exceeds the allowed area limits. Please adjust width and height\n so the total area remains within the defined range of {0} to {1} {2}².',
                                                                'Admin'
                                                            ),
                                                            editableModelOptions.minArea || '0',
                                                            editableModelOptions.maxArea || '0',
                                                            unit.label
                                                        )
                                            }
                                            type='number'
                                            step='0.01'
                                            min={0}
                                            placeholder='10'
                                            value={editableModelOptions.startWidth}
                                            fullWidth
                                            onChange={(e) => setEditableModelOptions((prev) => ({ ...prev, startWidth: e.target.value }))}
                                        />
                                    </FormControl>
                                    <FormControl label={T._('Starting Height', 'Admin')} caption={T._('Set the default starting height dimension', 'Admin')}>
                                        <InputWithValidation
                                            validationKey={'validationResizableStartHeightArea'}
                                            validate={(value) => {
                                                if (validateRequired(value, false) || parseFloat(value) <= 0) {
                                                    return true;
                                                }

                                                const height = parseFloat(value);
                                                const width = parseFloat(editableModelOptions.startWidth);
                                                const minArea = parseFloat(editableModelOptions.minArea);
                                                const maxArea = parseFloat(editableModelOptions.maxArea);

                                                if (!isNaN(width) && !isNaN(height) && !isNaN(minArea) && !isNaN(maxArea)) {
                                                    const currentArea = width * height;
                                                    if (currentArea < minArea || currentArea > maxArea) {
                                                        return true;
                                                    }
                                                }

                                                if (!isStepAligned(height, parseFloat(editableModelOptions.stepHeight))) {
                                                    return true;
                                                }

                                                return false;
                                            }}
                                            message={
                                                validateRequired(editableModelOptions.startHeight, false) || parseFloat(editableModelOptions.startHeight) <= 0
                                                    ? T._('Required', 'Admin')
                                                    : !isStepAligned(parseFloat(editableModelOptions.startHeight), parseFloat(editableModelOptions.stepHeight))
                                                      ? T._('The value must match the step increment', 'Admin')
                                                      : stringFormat(
                                                            T._(
                                                                'The starting size exceeds the allowed area limits. Please adjust width and height\n so the total area remains within the defined range of {0} to {1} {2}².',
                                                                'Admin'
                                                            ),
                                                            editableModelOptions.minArea || '0',
                                                            editableModelOptions.maxArea || '0',
                                                            unit.label
                                                        )
                                            }
                                            type='number'
                                            step='0.01'
                                            min={0}
                                            placeholder='5'
                                            value={editableModelOptions.startHeight}
                                            fullWidth
                                            onChange={(e) => setEditableModelOptions((prev) => ({ ...prev, startHeight: e.target.value }))}
                                        />
                                    </FormControl>
                                </Columns>
                            </ColumnWithBorder>
                        ) : (
                            <>
                                <ColumnWithBorder>
                                    <Columns $xs={2}>
                                        <FormControl
                                            label={T._('Minimum Measure', 'Admin')}
                                            caption={T._('Set the minimum measure value, which is the same for both dimensions (width and height)', 'Admin')}
                                        >
                                            <InputWithValidation
                                                validationKey={'validationResizableMinSide'}
                                                validate={(value) =>
                                                    !editableModelOptions.lockWidth &&
                                                    (validateRequired(value, false) ||
                                                        parseFloat(value) <= 0 ||
                                                        !isStepAligned(parseFloat(value), parseFloat(editableModelOptions.stepWidth)))
                                                }
                                                message={
                                                    !editableModelOptions.lockWidth &&
                                                    editableModelOptions.minWidth != '' &&
                                                    editableModelOptions.stepWidth != '' &&
                                                    !isStepAligned(parseFloat(editableModelOptions.minWidth), parseFloat(editableModelOptions.stepWidth))
                                                        ? T._('The value must match the step increment', 'Admin')
                                                        : T._('Required', 'Admin')
                                                }
                                                type='number'
                                                step='0.01'
                                                min={0}
                                                placeholder='1'
                                                value={editableModelOptions.minWidth}
                                                disabled={editableModelOptions.lockWidth}
                                                fullWidth
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setEditableModelOptions((prev) => ({
                                                        ...prev,
                                                        minWidth: value,
                                                        minHeight: value
                                                    }));
                                                }}
                                            />
                                        </FormControl>
                                        <FormControl
                                            label={editableModelOptions.lockWidth ? T._('Measure', 'Admin') : T._('Starting measure', 'Admin')}
                                            caption={T._(
                                                'Set the default starting measure value, which is the same for both dimensions (width and height).',
                                                'Admin'
                                            )}
                                        >
                                            <InputWithValidation
                                                validationKey={'validationResizableStartSide'}
                                                validate={(value) => {
                                                    // Check if value is empty or less than or equal to zero
                                                    if (validateRequired(value, false) || parseFloat(value) <= 0) {
                                                        return true;
                                                    }

                                                    // Only validate min/max constraints if width is not locked
                                                    if (!editableModelOptions.lockWidth) {
                                                        const parsedValue = parseFloat(value);
                                                        const maxWidth = parseFloat(editableModelOptions.maxWidth);
                                                        const minWidth = parseFloat(editableModelOptions.minWidth);
                                                        const maxSizeForSide = parseFloat(editableModelOptions.maxSizeForSide);

                                                        // Check if value exceeds maximum
                                                        const isGreaterThanMax = !isNaN(maxWidth) && maxWidth > 0 && parsedValue > maxWidth;
                                                        const isGreaterThanMaxSizeForSide =
                                                            !isNaN(maxSizeForSide) && maxSizeForSide > 0 && parsedValue > maxSizeForSide;
                                                        const isLessThanMin = !isNaN(minWidth) && minWidth > 0 && parsedValue < minWidth;

                                                        const stepAligned = isStepAligned(parsedValue, parseFloat(editableModelOptions.stepWidth));

                                                        return isGreaterThanMax || isGreaterThanMaxSizeForSide || isLessThanMin || !stepAligned;
                                                    }

                                                    return false;
                                                }}
                                                message={
                                                    validateRequired(editableModelOptions.startWidth, false) || parseFloat(editableModelOptions.startWidth) <= 0
                                                        ? T._('Required', 'Admin')
                                                        : !isStepAligned(
                                                                parseFloat(editableModelOptions.startWidth),
                                                                parseFloat(editableModelOptions.stepWidth)
                                                            )
                                                          ? T._('The value must match the step increment', 'Admin')
                                                          : T._('The value should be higher than the minimum and lower than the maximum measures.', 'Admin')
                                                }
                                                type='number'
                                                step='0.01'
                                                min={0}
                                                placeholder='10'
                                                value={editableModelOptions.startWidth}
                                                fullWidth
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setEditableModelOptions((prev) => ({
                                                        ...prev,
                                                        startWidth: value,
                                                        startHeight: value
                                                    }));
                                                }}
                                            />
                                        </FormControl>
                                    </Columns>
                                    <Columns $xs={2}>
                                        <FormControl
                                            label={T._('Maximum Measure', 'Admin')}
                                            caption={
                                                <>
                                                    {T._('Set the maximum value that must be respected by either the width or the height.', 'Admin')}
                                                    <br />
                                                    &nbsp;
                                                </>
                                            }
                                        >
                                            <InputWithValidation
                                                placeholder={T._('Maximum Measure', 'Admin')}
                                                value={editableModelOptions.maxSizeForSide}
                                                onChange={(e) => {
                                                    setEditableModelOptions((prev) => ({ ...prev, maxSizeForSide: e.target.value }));
                                                }}
                                                min={1}
                                                step={0.01}
                                                type='number'
                                                validationKey={'validationResizableMaxSizeForSide'}
                                                validate={(value) => {
                                                    const required = validateRequired(value, false);
                                                    const minWidth = parseFloat(editableModelOptions.minWidth);
                                                    const maxWidth = parseFloat(editableModelOptions.maxWidth);
                                                    const currentValue = parseFloat(value);

                                                    if (required) {
                                                        return true;
                                                    }

                                                    if (!isNaN(minWidth) && !isNaN(currentValue) && !isNaN(maxWidth)) {
                                                        console.log('startWidth', minWidth, 'currentValue', currentValue);
                                                        if (minWidth > currentValue || maxWidth < currentValue) {
                                                            return true;
                                                        }
                                                    }

                                                    if (!isStepAligned(currentValue, parseFloat(editableModelOptions.stepWidth))) {
                                                        return true;
                                                    }

                                                    return false;
                                                }}
                                                message={
                                                    validateRequired(editableModelOptions.maxSizeForSide, false)
                                                        ? T._('Required', 'Admin')
                                                        : !isStepAligned(
                                                                parseFloat(editableModelOptions.maxSizeForSide),
                                                                parseFloat(editableModelOptions.stepWidth)
                                                            )
                                                          ? T._('The value must match the step increment', 'Admin')
                                                          : T._(
                                                                'This value should be higher than minimum measure and lower than max measure for longest side.',
                                                                'Admin'
                                                            )
                                                }
                                            />
                                        </FormControl>

                                        <FormControl
                                            label={T._('Maximum - Other Side', 'Admin')}
                                            caption={T._(
                                                'Set the maximum value for the remaining side (width or height). This limit defines the range within which the other side must stay, based on the limit of the first side',
                                                'Admin'
                                            )}
                                        >
                                            <InputWithValidation
                                                validationKey={'validationResizableMaxSide'}
                                                validate={(value) =>
                                                    !editableModelOptions.lockWidth &&
                                                    (validateRequired(value, false) ||
                                                        parseFloat(value) <= 0 ||
                                                        !isStepAligned(parseFloat(value), parseFloat(editableModelOptions.stepWidth)))
                                                }
                                                message={
                                                    !editableModelOptions.lockWidth &&
                                                    editableModelOptions.maxWidth != '' &&
                                                    editableModelOptions.stepWidth != '' &&
                                                    !isStepAligned(parseFloat(editableModelOptions.maxWidth), parseFloat(editableModelOptions.stepWidth))
                                                        ? T._('The value must match the step increment', 'Admin')
                                                        : T._('Required', 'Admin')
                                                }
                                                type='number'
                                                step='0.01'
                                                min={0}
                                                placeholder='Maximum - Other Side'
                                                value={editableModelOptions.maxWidth}
                                                disabled={editableModelOptions.lockWidth}
                                                fullWidth
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setEditableModelOptions((prev) => ({
                                                        ...prev,
                                                        maxWidth: value,
                                                        maxHeight: value
                                                    }));
                                                }}
                                            />
                                        </FormControl>
                                    </Columns>
                                </ColumnWithBorder>
                            </>
                        )}

                        {constraintType === 'different' && (
                            <FormControlWithMarginTop>
                                <Checkbox
                                    checked={editableModelOptions?.keepAspectRatio || false}
                                    onChange={(checked) => handleClickKeepAspectRatio(checked)}
                                    disabled={editableModelOptions.lockWidth || editableModelOptions.lockHeight}
                                    label={T._('Keep aspect ratio', 'Admin')}
                                    caption={T._(
                                        `When enabled, the product's dimensions will always scale proportionally, maintaining the original aspect ratio. This means your customers won't be able to switch between shape types — for example, a rectangle can't become a square, and a circle can't become an oval.`,
                                        'Admin'
                                    )}
                                />
                            </FormControlWithMarginTop>
                        )}

                        <Column style={{ gap: 0 }}>
                            <ParagraphTitle style={{ marginTop: 30 }}>{T._('Step Adjustment', 'Admin')}</ParagraphTitle>
                            <ParagraphSubTitle>
                                {T._(
                                    'Set the step value for resizing, allowing customers to choose dimensions in specific increments. For example, if the product can be between 10 and 50 cm with a step of 10 cm, customers can select sizes like 10 cm, 20 cm, 30 cm, 40 cm, and 50 cm.',
                                    'Admin'
                                )}
                            </ParagraphSubTitle>
                            <Columns $xs={6}>
                                {/* Same step for width and height */}
                                <FormControl>
                                    <InputWithValidation
                                        validationKey={'validationResizableStep'}
                                        validate={(value) =>
                                            validateRequired(value, false) ||
                                            parseFloat(value) <= 0 ||
                                            (!editableModelOptions.lockWidth &&
                                                parseFloat(editableModelOptions.maxWidth) > 0 &&
                                                parseFloat(value) > parseFloat(editableModelOptions.maxWidth)) ||
                                            (!editableModelOptions.lockHeight &&
                                                parseFloat(editableModelOptions.maxHeight) > 0 &&
                                                parseFloat(value) > parseFloat(editableModelOptions.maxHeight))
                                        }
                                        message={
                                            validateRequired(editableModelOptions.stepWidth, false) || parseFloat(editableModelOptions.stepWidth) <= 0
                                                ? T._('Required', 'Admin')
                                                : T._('the value should be within the specified limits', 'Admin')
                                        }
                                        type='number'
                                        step='0.01'
                                        min={0}
                                        placeholder='1'
                                        value={editableModelOptions.stepWidth}
                                        fullWidth
                                        onChange={(e) =>
                                            setEditableModelOptions((prev) => ({
                                                ...prev,
                                                stepWidth: e.target.value,
                                                stepHeight: e.target.value
                                            }))
                                        }
                                    />
                                </FormControl>
                            </Columns>
                        </Column>
                    </ResizableProductSection>
                </ResizableSectionContent>

                {/* Background and Border Section */}
                <ResizableSectionContent $visible={activeMainSection === 'background-border'}>
                    <ResizableProductSection>
                        <ParagraphTitle>{T._('Background ', 'Admin')}</ParagraphTitle>
                        <ParagraphSubTitle>
                            {T._(`Choose whether the product has a `, 'Admin')}
                            <Link href='https://zakeke.zendesk.com/hc/en-us/articles/21841748466076' target='_blank'>
                                {T._('background', 'Admin')}
                            </Link>
                            {T._(` as a solid color or a material (e.g. wood, marble, etc.). If not enabled, the background will be plain white.`, 'Admin')}
                        </ParagraphSubTitle>
                        <ProductBackgroundSection editableModelOptions={editableModelOptions} setEditableModelOptions={setEditableModelOptions} />
                    </ResizableProductSection>

                    <ResizableProductSection>
                        <ParagraphTitle>{T._('Border', 'Admin')}</ParagraphTitle>
                        <ParagraphSubTitle>
                            <Link
                                href='https://zakeke.zendesk.com/hc/en-us/articles/21841748466076-Background-and-Border-for-Resizable-Products'
                                target='_blank'
                            >
                                {T._('Add a border', 'Admin')}
                            </Link>
                            {T._(
                                ` to your product. Configure the border thickness and decide if it is filled with a color or extends the customer's design.`,
                                'Admin'
                            )}
                        </ParagraphSubTitle>
                        <Column>
                            <Columns $xs={4}>
                                <FormControl
                                    label={T._('Border type', 'Admin')}
                                    tooltipLabelHtml={
                                        <span>
                                            {T._(
                                                `Please select your preferred border type. "Solid Color" means the border will have a uniform color of your choice, 
										and your customers won't be able to change it. "Mirror Wrap" means the customer's design will be mirrored and extended 
										onto the border of the canvas. See the example below:`,
                                                'Admin'
                                            )}
                                            <StyledImg src={borderTypeExampleImage} />
                                        </span>
                                    }
                                >
                                    <div style={{ marginBottom: '17px' }}>
                                        <AdvancedSelect
                                            isClearable={false}
                                            value={edgeTypeOptions.find((o) => o.value === editableModelOptions.mirrorType)}
                                            components={{
                                                Option: BorderOption
                                            }}
                                            options={
                                                editableModelOptions?.backgroundType !== 1
                                                    ? edgeTypeOptionsToDisplay.filter((opt) => opt.value !== 'NONE')
                                                    : edgeTypeOptionsToDisplay
                                            }
                                            menuPosition='fixed'
                                            onChange={(option) => {
                                                setEditableModelOptions((prev) => ({
                                                    ...prev,
                                                    mirrorType: option!.value as ProductMirrorTypes,
                                                    edgesColor: option!.value === 'COLORED' ? prev.edgesColor : '#FFFFFF',
                                                    includeBorderInPrintFiles: option!.value === 'MIRRORED' ? true : false
                                                }));
                                            }}
                                        />
                                    </div>
                                </FormControl>
                                <FormControl label={T._('Border thickness', 'Admin')}>
                                    <InputWithValidation
                                        validationKey={'validationResizableDepth'}
                                        validate={(value) => validateRequired(value, false) || parseFloat(value) <= 0}
                                        message={T._('Required', 'Admin')}
                                        type='number'
                                        step='0.01'
                                        min={0}
                                        placeholder='1'
                                        value={editableModelOptions.depth}
                                        fullWidth
                                        onChange={(e) => setEditableModelOptions((prev) => ({ ...prev, depth: e.target.value }))}
                                    />
                                </FormControl>
                                {editableModelOptions.mirrorType === 'COLORED' && (
                                    <FormControl label={`${T._('Color', 'Admin')}`}>
                                        <ColorSelection
                                            isFormFieldSelection
                                            colors={[editableModelOptions.edgesColor ?? '#FFFFFF']}
                                            menuPosition='fixed'
                                            onChange={(_, color) =>
                                                setEditableModelOptions((prev) => ({
                                                    ...prev,
                                                    edgesColor: color
                                                }))
                                            }
                                        />
                                    </FormControl>
                                )}
                            </Columns>
                            <FormControl>
                                <Checkbox
                                    checked={editableModelOptions?.includeBorderInPrintFiles || false}
                                    onChange={(checked) => handleIncludeBorderInPrintFiles(checked)}
                                    label={T._('Include Border in Print Files', 'Admin')}
                                    caption={T._(
                                        `Check this box to include the border in the print files; otherwise, it will only be visible in the 3D product view.`,
                                        'Admin'
                                    )}
                                />
                            </FormControl>
                        </Column>
                        <BannerContainer>
                            <NotificationBanner
                                type={'INFO'}
                                content={
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: `The border is only visible in the 3D preview within the customizer. It will not appear in the 2D view. <strong>Make sure to enable the 3D preview in the next step</strong>, otherwise the border will not be displayed and will only be included in the print file if “Include Border in Print Files” is checked.`
                                        }}
                                    />
                                }
                            ></NotificationBanner>
                        </BannerContainer>
                    </ResizableProductSection>
                </ResizableSectionContent>

                {/* Multi Canvas Section */}
                <ResizableSectionContent $visible={activeMainSection === 'multi-canvas'}>
                    <ResizableProductSection>
                        <SwitchGroup
                            checked={editableModelOptions?.isSplitOutput || false}
                            label={T._('Multi-canvas', 'Admin')}
                            helpTooltipPosition='top'
                            helpToolTip={
                                editableModelOptions.shape == 1 ? (
                                    <>{T._('Multi-canvas is disabled cause you have selected a circular shape.', 'Admin')}</>
                                ) : null
                            }
                            caption={
                                <>
                                    <label>
                                        {T._(
                                            `Enable customers to split the resizable product into multiple columns and rows to create a collage. Configure the minimum dimensions for columns and rows.`,
                                            'Admin'
                                        )}
                                    </label>{' '}
                                    <Link target='_blank' href='https://zakeke.zendesk.com/hc/en-us/articles/21841758992540'>
                                        {T._('Learn more', 'Admin')}
                                    </Link>
                                </>
                            }
                            disabled={editableModelOptions.shape == 1}
                            onChange={(checked) => {
                                validation.clear();
                                setEditableModelOptions((prev) => ({
                                    ...prev,
                                    isSplitOutput: checked
                                }));
                            }}
                        />

                        {editableModelOptions.isSplitOutput && (
                            <>
                                <Columns $xs={2} style={{ marginTop: '16px', marginLeft: '48px' }}>
                                    <ColumnWithBorder>
                                        <ParagraphTitle>{T._('Columns', 'Admin')}</ParagraphTitle>
                                        <Columns $xs={2} style={{ marginTop: '10px' }}>
                                            <FormControl label={`${T._('Min width', 'Admin')}`}>
                                                <InputWithValidation
                                                    validationKey={'validationMinColumnWidth'}
                                                    validate={(value) =>
                                                        validateRequired(value) ||
                                                        parseFloat(value) <= 0 ||
                                                        (!editableModelOptions.lockWidth &&
                                                            parseFloat(editableModelOptions.minWidth) > 0 &&
                                                            parseFloat(value) > parseFloat(editableModelOptions.minWidth))
                                                    }
                                                    message={
                                                        validateRequired(editableModelOptions.minSplitWidth, false) ||
                                                        parseFloat(editableModelOptions.minSplitWidth) <= 0
                                                            ? T._('Required', 'Admin')
                                                            : T._('The value should be lower than the\nminimum width', 'Admin')
                                                    }
                                                    type='number'
                                                    step='1'
                                                    min={1}
                                                    value={editableModelOptions.minSplitWidth}
                                                    onChange={(e) =>
                                                        setEditableModelOptions((prev) => ({
                                                            ...prev,
                                                            minSplitWidth: e.target.value
                                                        }))
                                                    }
                                                />
                                            </FormControl>
                                            <FormControl label={`${T._('Starting number of columns', 'Admin')}`}>
                                                <InputWithValidation
                                                    validationKey={'validationStartColumns'}
                                                    validate={(value) => validateRequired(value) || parseFloat(value) <= 0}
                                                    message={T._('Required', 'Admin')}
                                                    type='number'
                                                    step='1'
                                                    min={1}
                                                    value={editableModelOptions.startSplitColumns}
                                                    onChange={(e) =>
                                                        setEditableModelOptions((prev) => ({
                                                            ...prev,
                                                            startSplitColumns: e.target.value
                                                        }))
                                                    }
                                                />
                                            </FormControl>
                                        </Columns>
                                    </ColumnWithBorder>
                                    <ColumnWithBorder>
                                        <ParagraphTitle>{T._('Rows', 'Admin')}</ParagraphTitle>
                                        <Columns $xs={2} style={{ marginTop: '10px' }}>
                                            <FormControl label={`${T._('Min height', 'Admin')}`}>
                                                <InputWithValidation
                                                    validationKey={'validationMinRowHeight'}
                                                    validate={(value) =>
                                                        validateRequired(value) ||
                                                        parseFloat(value) <= 0 ||
                                                        (!editableModelOptions.lockHeight &&
                                                            parseFloat(editableModelOptions.minHeight) > 0 &&
                                                            parseFloat(value) > parseFloat(editableModelOptions.minHeight))
                                                    }
                                                    message={
                                                        validateRequired(editableModelOptions.minSplitHeight, false) ||
                                                        parseFloat(editableModelOptions.minSplitHeight) <= 0
                                                            ? T._('Required', 'Admin')
                                                            : T._('The value should be lower than the\nminimum height', 'Admin')
                                                    }
                                                    type='number'
                                                    step='1'
                                                    min={1}
                                                    value={editableModelOptions.minSplitHeight}
                                                    onChange={(e) =>
                                                        setEditableModelOptions((prev) => ({
                                                            ...prev,
                                                            minSplitHeight: e.target.value
                                                        }))
                                                    }
                                                />
                                            </FormControl>
                                            <FormControl label={`${T._('Starting number of rows', 'Admin')}`}>
                                                <InputWithValidation
                                                    validationKey={'validationStartRows'}
                                                    validate={(value) => validateRequired(value) || parseFloat(value) <= 0}
                                                    message={T._('Required', 'Admin')}
                                                    type='number'
                                                    step='1'
                                                    min={1}
                                                    value={editableModelOptions.startSplitRows}
                                                    onChange={(e) =>
                                                        setEditableModelOptions((prev) => ({
                                                            ...prev,
                                                            startSplitRows: e.target.value
                                                        }))
                                                    }
                                                />
                                            </FormControl>
                                        </Columns>
                                    </ColumnWithBorder>
                                </Columns>
                            </>
                        )}
                    </ResizableProductSection>
                </ResizableSectionContent>

                {/* 3D Preview Section */}
                <ResizableSectionContent $visible={activeMainSection === '3d-preview'}>
                    <ResizableProductSection>
                        <SwitchGroup
                            checked={editableModelOptions?.is3DAutoGenEnabled || false}
                            label={T._('3D preview', 'Admin')}
                            caption={T._(
                                `If checked, Zakeke will automatically generate a 3D visualization of your resizable product along with displaying the 2D version`,
                                'Admin'
                            )}
                            disabled={false}
                            onChange={(checked) => handleClick3DPreview(checked)}
                        />

                        {editableModelOptions?.is3DAutoGenEnabled && (
                            <Columns style={{ marginTop: '16px', marginLeft: '48px' }} $xs={1}>
                                <Checkbox
                                    checked={state.product.sceneConfig?.general?.arEnabled || false}
                                    onChange={(checked) => handleClickIsArEnabled(checked)}
                                    label={T._('Augmented Reality', 'Admin')}
                                    caption={T._(`Your customers can view your product in the real world through their smartphones.`, 'Admin')}
                                />
                            </Columns>
                        )}
                    </ResizableProductSection>
                </ResizableSectionContent>

                {/* Preset Options Section */}
                <ResizableSectionContent $visible={activeMainSection === 'preset-options'}>
                    <ResizableProductSection>
                        <ParagraphTitle>{T._('Preset options', 'Admin')}</ParagraphTitle>
                        <ParagraphSubTitle>
                            {T._('With preset options, you can offer your customers a range of predefined starting measurements to choose from. ', 'Admin')}
                            <Link href='https://zakeke.zendesk.com/hc/en-us/articles/21841801644828' target='_blank'>
                                {T._('Learn more.', 'Admin')}
                            </Link>
                        </ParagraphSubTitle>
                        <PresetsRowHeaderContainer>
                            <Column>
                                {(editableModelOptions.presets == null || editableModelOptions.presets?.length === 0) && (
                                    <LinkStyled onClick={() => handleAddPreset(0)}>
                                        <span>{T._('Add preset', 'Admin')}</span>
                                    </LinkStyled>
                                )}
                            </Column>
                        </PresetsRowHeaderContainer>
                        <PresetColumn>
                            {editableModelOptions.presets?.map((preset, i) => (
                                <PresetsRowContainer key={i}>
                                    <FormControl label={T._('Select preset option', 'Admin')}>
                                        <AdvancedSelect
                                            isSearchable={false}
                                            options={[
                                                {
                                                    value: '',
                                                    label: 'Custom'
                                                },
                                                ...presets
                                            ]}
                                            placeholder={T._('Select preset', 'Admin')}
                                            onChange={(e) => handleChangePresetType(e as (typeof presets)[0], i)}
                                            isMulti={false}
                                            value={presets.find((x) => x.value === preset.name) || { value: 'Custom', label: 'Custom' }}
                                        />
                                    </FormControl>
                                    <FormControl label={T._('Name', 'Admin')}>
                                        <InputWithValidation
                                            validationKey={'validationPresetName' + i}
                                            validate={(value) => validateRequired(value) || !validateInputSecurity(value).isValid}
                                            message={`${validateRequired(preset.name) ? T._('Required', 'Admin') : !validateInputSecurity(preset.name).isValid ? T._(`Invalid characters detected: ${validateInputSecurity(preset.name).invalidCharacters.join(', ')}`, 'Admin') : ''}`}
                                            type='text'
                                            value={preset.name}
                                            onChange={(e) => handleChangePresetName(e.target.value, i)}
                                            disabled={!!presets.find((x) => x.value === preset.name)}
                                        />
                                    </FormControl>
                                    <FormControl label={T._('Width', 'Admin')}>
                                        <InputWithValidation
                                            validationKey={'validationPresetWidth' + i}
                                            validate={validateRequired}
                                            message={T._('Required', 'Admin')}
                                            type='number'
                                            step='0.01'
                                            min={0}
                                            placeholder='1'
                                            value={preset.width}
                                            onChange={(e) => handleChangePresetWidth(e.target.value, i)}
                                            disabled={!!presets.find((x) => x.value === preset.name)}
                                        />
                                    </FormControl>
                                    <FormControl label={T._('Height', 'Admin')}>
                                        <InputWithValidation
                                            validationKey={'validationPresetHeight' + i}
                                            validate={validateRequired}
                                            message={T._('Required', 'Admin')}
                                            type='number'
                                            step='0.01'
                                            min={0}
                                            placeholder='1'
                                            value={preset.height}
                                            onChange={(e) => handleChangePresetHeight(e.target.value, i)}
                                            disabled={!!presets.find((x) => x.value === preset.name)}
                                        />
                                    </FormControl>
                                    <IconContainer>
                                        <Icon onClick={() => handleDeletePreset(i)}>
                                            <MdDelete cursor={'pointer'} />
                                        </Icon>
                                        <VerticalSeparator />
                                        <Icon disabled={i !== editableModelOptions.presets!.length - 1} onClick={() => handleAddPreset(i)}>
                                            <AddIcon cursor={i !== editableModelOptions.presets!.length - 1 ? 'unset' : 'pointer'} />
                                        </Icon>
                                    </IconContainer>
                                </PresetsRowContainer>
                            ))}
                        </PresetColumn>
                    </ResizableProductSection>
                </ResizableSectionContent>

                {/* Save Button - Always visible */}
                <PageFooterButtons $fixed={true}>
                    <PrimaryButton
                        style={{ marginLeft: 'auto' }}
                        size='medium'
                        onClick={() => {
                            if (!validation.isValid()) {
                                showErrorNotification({
                                    message: T._('Some fields are invalid.', 'Admin')
                                });
                                return;
                            }

                            // Clear values from unused constraint types before saving
                            let cleanedEditableModelOptions = { ...editableModelOptions };

                            if (constraintType === 'different') {
                                // Clear flexible constraint values
                                cleanedEditableModelOptions.maxSizeForSide = '';
                                // Clear area constraint values
                                cleanedEditableModelOptions.minArea = '';
                                cleanedEditableModelOptions.maxArea = '';
                            } else if (constraintType === 'flexibile') {
                                // Clear area constraint values
                                cleanedEditableModelOptions.minArea = '';
                                cleanedEditableModelOptions.maxArea = '';
                            } else if (constraintType === 'area') {
                                // Clear different constraint values
                                cleanedEditableModelOptions.minWidth = '';
                                cleanedEditableModelOptions.maxWidth = '';
                                cleanedEditableModelOptions.minHeight = '';
                                cleanedEditableModelOptions.maxHeight = '';
                                // Clear flexible constraint values
                                cleanedEditableModelOptions.maxSizeForSide = '';
                            }

                            const modelOptions = editableModelOptionsToModelOptions(cleanedEditableModelOptions, modelSide.ppcm, unit);

                            const sides = modelColor.sides;

                            handleResizableAreaChanged(modelOptions.startWidth ?? 0, modelOptions.startHeight ?? 0);

                            if (sides.length === 1 || (sides.length > 1 && (sideType === 'double-syn' || sideType === 'multiplesyn'))) {
                                const newOptions = { ...modelOptions, sideGuid: null };
                                onSave([newOptions]);
                            } else {
                                const newOptions = sides.map((side, idx) => {
                                    return {
                                        ...modelOptions,
                                        optionID: idx !== 0 ? null : modelOptions.optionID,
                                        sideGuid: side.guid
                                    };
                                });

                                onSave(newOptions);
                            }
                        }}
                    >
                        {T._('Go to review', 'Admin')}
                    </PrimaryButton>
                </PageFooterButtons>
            </PageMenuContentContainer>
        </PageMenuLayoutContainer>
    );
};

const ResizablePrintArea = ({ onSave }: IResizablePrintAreaProps) => {
    const { state } = useProductConfigurationState();
    const [modelColor] = getResizableModelAttributes(state.product);

    const buildSideType = () => {
        if (modelColor.sides.length === 1) {
            return 'single';
        } else if (modelColor.sides.length === 2 && state.product.options?.length === 1 && state.product.options[0].sideGuid === null) {
            return 'double-syn';
        } else if (modelColor.sides.length > 1 && state.product.options?.length === 1 && state.product.options[0].sideGuid === null) {
            return 'multiplesyn';
        } else {
            return 'multiple';
        }
    };

    const [sideType, setSideType] = useState<ResizableSideType>(buildSideType());

    return (
        <ValidationStateProvider>
            <ResizablePrintAreaContextProvider initialSideType={sideType || 'single'}>
                <Suspense fallback={<OverlayLoading forceShow />}>
                    <ResizablePrintAreaContent onSave={onSave} />
                </Suspense>
            </ResizablePrintAreaContextProvider>
        </ValidationStateProvider>
    );
};

export default ResizablePrintArea;
